import { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Bell,
  CheckCheck,
  Settings,
  UserCheck,
  AtSign,
  AlertTriangle,
  AlertCircle,
  Inbox,
} from "lucide-react";
import { trpc } from "@/lib/trpc";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Button } from "@/components/ui/button";
import { SimpleTooltip } from "@/components/ui/tooltip";
import { Badge } from "@/components/ui/badge";
import { formatRelativeTime } from "@/lib/freshness";
import { cn } from "@/lib/utils";
import {
  NotificationPreferencesDialog,
  type NotificationKind,
} from "./notification-preferences-dialog";

function NotificationIcon({ kind }: { kind: string }) {
  switch (kind as NotificationKind) {
    case "finding-assigned":
      return <UserCheck className="h-4 w-4 text-blue-500 shrink-0 mt-0.5" />;
    case "mentioned":
      return <AtSign className="h-4 w-4 text-purple-500 shrink-0 mt-0.5" />;
    case "regressed":
      return (
        <AlertTriangle className="h-4 w-4 text-rose-500 shrink-0 mt-0.5" />
      );
    case "tracker-conflict":
      return (
        <AlertCircle className="h-4 w-4 text-orange-500 shrink-0 mt-0.5" />
      );
    default:
      return <Bell className="h-4 w-4 text-muted-foreground shrink-0 mt-0.5" />;
  }
}

export function NotificationBell({ className }: { className?: string }) {
  const [open, setOpen] = useState(false);
  const [unreadOnly, setUnreadOnly] = useState(false);
  const [prefsOpen, setPrefsOpen] = useState(false);
  const navigate = useNavigate();

  const utils = trpc.useUtils();

  const { data: countData } = trpc.notification.unreadCount.useQuery(
    undefined,
    {
      refetchInterval: 30_000,
    },
  );
  const unreadCount = countData?.count ?? 0;

  const { data: listData, isLoading } = trpc.notification.list.useQuery(
    { unreadOnly, limit: 30 },
    { enabled: open },
  );

  const markReadMutation = trpc.notification.markRead.useMutation({
    onSuccess: () => {
      utils.notification.unreadCount.invalidate();
      utils.notification.list.invalidate();
    },
  });

  const markAllReadMutation = trpc.notification.markAllRead.useMutation({
    onSuccess: () => {
      utils.notification.unreadCount.invalidate();
      utils.notification.list.invalidate();
    },
  });

  const notifications = listData?.notifications ?? [];

  const handleItemClick = (item: {
    id: string;
    readAt: string | Date | null;
    linkUrl: string | null;
  }) => {
    if (!item.readAt) {
      markReadMutation.mutate({ ids: [item.id] });
    }
    if (item.linkUrl) {
      setOpen(false);
      navigate(item.linkUrl);
    }
  };

  return (
    <>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <button
            type="button"
            className={cn(
              "relative flex items-center justify-center rounded-lg p-2 text-sidebar-muted-foreground transition-colors hover:bg-sidebar-accent/60 hover:text-sidebar-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring",
              className,
            )}
            aria-label={`Notifications${unreadCount > 0 ? ` (${unreadCount} unread)` : ""}`}
          >
            <Bell className="h-4 w-4" />
            {unreadCount > 0 && (
              <span className="absolute top-1 right-1 flex h-4 min-w-4 items-center justify-center rounded-full bg-primary px-1 text-xs font-bold leading-none text-primary-foreground shadow-sm">
                {unreadCount > 99 ? "99+" : unreadCount}
              </span>
            )}
          </button>
        </PopoverTrigger>

        <PopoverContent
          align="start"
          side="top"
          className="w-[380px] p-0 shadow-lg border border-border bg-popover"
        >
          {/* Header */}
          <div className="flex items-center justify-between border-b border-border/60 px-4 py-3">
            <div className="flex items-center gap-2">
              <span className="text-sm font-semibold text-foreground">
                Notifications
              </span>
              {unreadCount > 0 && (
                <Badge
                  variant="secondary"
                  className="h-5 px-1.5 text-xs font-semibold"
                >
                  {unreadCount} new
                </Badge>
              )}
            </div>
            <div className="flex items-center gap-1">
              {unreadCount > 0 && (
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-7 px-2 text-xs text-muted-foreground hover:text-foreground"
                  onClick={() => markAllReadMutation.mutate()}
                  disabled={markAllReadMutation.isPending}
                >
                  <CheckCheck className="h-3.5 w-3.5 mr-1" />
                  Mark read
                </Button>
              )}
              <SimpleTooltip content="Notification preferences">
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-7 w-7 text-muted-foreground hover:text-foreground"
                  onClick={() => {
                    setOpen(false);
                    setPrefsOpen(true);
                  }}
                  aria-label="Notification preferences"
                >
                  <Settings className="h-3.5 w-3.5" />
                </Button>
              </SimpleTooltip>
            </div>
          </div>

          {/* Filter Bar */}
          <div className="flex items-center border-b border-border/40 px-3 py-1.5 bg-muted/20 text-xs">
            <button
              type="button"
              onClick={() => setUnreadOnly(false)}
              className={cn(
                "rounded px-2 py-0.5 font-medium transition-colors",
                !unreadOnly
                  ? "bg-background text-foreground shadow-xs"
                  : "text-muted-foreground hover:text-foreground",
              )}
            >
              All
            </button>
            <button
              type="button"
              onClick={() => setUnreadOnly(true)}
              className={cn(
                "rounded px-2 py-0.5 font-medium transition-colors ml-1",
                unreadOnly
                  ? "bg-background text-foreground shadow-xs"
                  : "text-muted-foreground hover:text-foreground",
              )}
            >
              Unread
            </button>
          </div>

          {/* Notification List */}
          <div className="max-h-[380px] overflow-y-auto divide-y divide-border/40">
            {isLoading ? (
              <div className="p-8 text-center text-xs text-muted-foreground">
                Loading notifications...
              </div>
            ) : notifications.length === 0 ? (
              <div className="flex flex-col items-center justify-center p-8 text-center">
                <Inbox className="h-8 w-8 text-muted-foreground mb-2" />
                <p className="text-sm font-medium text-foreground">
                  No notifications
                </p>
                <p className="text-xs text-muted-foreground mt-0.5">
                  {unreadOnly
                    ? "You're all caught up with unread items."
                    : "You haven't received any notifications yet."}
                </p>
              </div>
            ) : (
              notifications.map((item) => {
                const isUnread = !item.readAt;
                return (
                  <button
                    key={item.id}
                    type="button"
                    onClick={() => handleItemClick(item)}
                    className={cn(
                      "flex w-full items-start gap-3 p-3 text-left transition-colors hover:bg-muted/40",
                      isUnread && "bg-primary/5",
                    )}
                  >
                    <NotificationIcon kind={item.kind} />
                    <div className="flex-1 min-w-0 leading-tight">
                      <div className="flex items-baseline justify-between gap-1 mb-1">
                        <span
                          className={cn(
                            "text-xs line-clamp-1",
                            isUnread
                              ? "font-semibold text-foreground"
                              : "font-normal text-muted-foreground",
                          )}
                        >
                          {item.title}
                        </span>
                        <span className="text-xs text-muted-foreground whitespace-nowrap shrink-0">
                          {formatRelativeTime(item.createdAt)}
                        </span>
                      </div>
                      {item.body && (
                        <p className="text-xs text-muted-foreground line-clamp-2 leading-relaxed">
                          {item.body}
                        </p>
                      )}
                    </div>
                    {isUnread && (
                      <span className="h-2 w-2 rounded-full bg-primary shrink-0 mt-1" />
                    )}
                  </button>
                );
              })
            )}
          </div>

          {/* Footer */}
          <div className="border-t border-border/60 px-4 py-2 bg-muted/10 flex items-center justify-between text-xs">
            <button
              type="button"
              className="text-muted-foreground hover:text-foreground transition-colors"
              onClick={() => {
                setOpen(false);
                setPrefsOpen(true);
              }}
            >
              Configure preferences
            </button>
          </div>
        </PopoverContent>
      </Popover>

      <NotificationPreferencesDialog
        open={prefsOpen}
        onOpenChange={setPrefsOpen}
      />
    </>
  );
}
